import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface ThemeInfo {
  id: string;
  label: string;
  swatch: string;
}

export const THEMES: ThemeInfo[] = [
  { id: 'carbon', label: 'Carbon', swatch: '#3ec6c6' },
  { id: 'obsidian', label: 'Obsidian', swatch: '#7c6cf0' },
  { id: 'bullion', label: 'Bullion', swatch: '#c8a050' },
  { id: 'bubblewav', label: 'Bubblewav', swatch: '#40c8e0' },
  { id: 'ultraviolet', label: 'Ultraviolet', swatch: '#b060e0' },
];

export type ThemeMode = 'dark' | 'light';

// The one theme used on landing / legal / auth pages and in every marketing
// asset (OG image, demo video, screenshots). Branding is built on visual
// consistency; marketing pages ignore user theme preference.
export const BRAND_THEME = 'carbon';
export const BRAND_MODE: ThemeMode = 'dark';

interface ThemeContextValue {
  theme: string;
  mode: ThemeMode;
  setTheme: (id: string) => void;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_KEY = 'smplecut-theme';
const MODE_KEY = 'smplecut-mode';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState(() => localStorage.getItem(THEME_KEY) || 'carbon');
  const [mode, setModeState] = useState<ThemeMode>(() => (localStorage.getItem(MODE_KEY) as ThemeMode) || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-mode', mode);
  }, [theme, mode]);

  const setTheme = useCallback((id: string) => {
    setThemeState(id);
    localStorage.setItem(THEME_KEY, id);
  }, []);

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    localStorage.setItem(MODE_KEY, m);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(MODE_KEY, next);
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, mode, setTheme, setMode, toggleMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
